import { ChangeDetectionStrategy, Component, Input, OnInit, ViewChild, inject } from '@angular/core'
import { SortDirection } from '@angular/material/sort'
import { Actions, ofType } from '@ngrx/effects'
import { Store, select } from '@ngrx/store'
import { AccountObject, DealRow, DealViewRawCosts, DealViewRawDeal, TableKey } from '@tradecafe/types/core'
import { DeepReadonly } from '@tradecafe/types/utils'
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed'
import { map as _map, identity, isEmpty, isEqual, orderBy, pick, pickBy, sortBy } from 'lodash-es'
import { BehaviorSubject, EMPTY, Observable, Subject, combineLatest, merge, of } from 'rxjs'
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap, take, withLatestFrom } from 'rxjs/operators'
import { selectAccountEntities } from 'src/app/store/accounts'
import { cloneDealOperationCompleted, cloneDealSuccess, copyCloneDealSuccess, loadDealViews, massDealFormSuccess, saveDealFormSuccess, sendConfirmationSuccess, setDestLocationSuccess, setOriginLocationSuccess } from 'src/app/store/deal-view.actions'
import { selectDealViewEntities, selectDealsTable } from 'src/app/store/deal-view.reducer'
import { CsvFormatterService } from 'src/components/csv-exporter/csv-formatter.service'
import { DealsListComponent, columnDefsById, columnNames } from 'src/components/deals-list'
import { DealElasticSearchService } from 'src/services/data/deal-elastic.service'
import { waitNotEmpty } from 'src/services/data/utils'
import { ElasticSearchFilters, FilterData, FilterDataSelect } from 'src/services/elastic-search'
import { FiltersFormGroup, FiltersService, TableFilters } from 'src/services/table-utils'
import { ApiTableDataSource } from 'src/services/table-utils/data-sources/api-table-data-source'
import { replayForm } from 'src/shared/utils/replay-form'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { CloneFormService } from '../../deal-clones/clone-form/clone-form.service'

interface CloneFiltersFormValue extends TableFilters { buyerId: number[], supplierId: number[], table_view_id: string }

export const FRONTEND_SORT_COLUMNS = ['seq', 'closingDate']

type CloneSelection = Pick<CloneFiltersFormValue, 'buyerId' | 'supplierId'>

function selectionOf({ buyerId, supplierId }: CloneSelection): CloneSelection {
  return { buyerId, supplierId }
}

function optionIds(filterData: FilterData) {
  return (filterData as FilterDataSelect)?.options?.map(option => option.id) || []
}

@Component({
  selector: 'tc-deal-clones-v2',
  templateUrl: './deal-clones.component.html',
  styleUrls: ['./deal-clones.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DealClonesComponent extends OnDestroyMixin implements OnInit {
  private store = inject(Store)
  private actions$ = inject(Actions)
  private csvFormatter = inject(CsvFormatterService)
  private CloneForm = inject(CloneFormService)
  private elastic = inject(DealElasticSearchService)
  private Filters = inject(FiltersService)
  private toaster = inject(ToasterService)


  // deals list configuration
  readonly exportColumns = ['seq', 'supplier', 'buyer', 'origin_location', 'shipment_date', 'delivery_date', 'buy_price', 'sell_price', 'partial_margin', 'deal_id', 'fx_bid_rate', 'fx_ask_rate']
  readonly displayColumns = ['select', 'seq', 'supplier', 'buyer', 'origin_location', 'shipment_date', 'closingDate', 'delivery_date', 'buy_price', 'sell_price', 'partial_margin', 'deal_id', 'clonesMenu']
  readonly tableIdentity = TableKey.DealClonesTab
  readonly columnNames = columnNames(this.tableIdentity)
  private readonly fetchColumns = [...this.exportColumns, 'bids', 'offers', 'segments']

  @Input() dealViewRaw$: Observable<DeepReadonly<DealViewRawDeal & DealViewRawCosts>>
  @ViewChild(DealsListComponent) dealsList: DealsListComponent

  readonly filtersForm: FiltersFormGroup<CloneFiltersFormValue> = this.Filters.buildForm<CloneFiltersFormValue>(this, {
    key: this.tableIdentity,
    defaultValues: {
      buyerId: [],
      supplierId: [],
      limit: 25,
      sort: undefined,
      table_view_id: undefined,
      columns: [...this.exportColumns, 'bids', 'offers', 'segments'],
    },
  })
  private cloneIds$: Observable<DeepReadonly<string[]>>
  private matched$: Observable<{ cloneIds: string[], ids: string[], aggregations: ElasticSearchFilters, selection: CloneSelection }>
  private closingDateFor: string[]
  private closingDateRows$: Observable<DealRow[]>
  // the ids to render, in the order the tab owns. undefined = the backend sorted this page,
  // so its own order (the reducer's window) is the one to show
  private displayIds$ = new BehaviorSubject<string[]>(undefined)
  // a party change leaves clone_ids untouched, so the aggregation has to be re-asked for explicitly
  private reloadAggregations$ = new BehaviorSubject<void>(undefined)
  // the async clone operation reports its own clone set, which the deal view does not carry yet
  private eventCloneIds$ = new Subject<string[]>()

  dataSource: ApiTableDataSource<DealRow, CloneFiltersFormValue>
  rows$: Observable<DealRow[]>
  // the order the tab is currently showing - the CSV export reads it instead of `dataSource.data`,
  // which now holds one page
  orderedIds: string[] = []

  private accounts$ = this.store.pipe(select(selectAccountEntities), waitNotEmpty())
  suppliers$: Observable<DeepReadonly<AccountObject[]>>
  buyers$: Observable<DeepReadonly<AccountObject[]>>
  showSupplierFilter$: Observable<boolean>
  showBuyerFilter$: Observable<boolean>
  showFilters$: Observable<boolean>
  canClone$: Observable<boolean>

  ngOnInit() {
    this.cloneIds$ = merge(
      this.dealViewRaw$.pipe(map(dv => dv.deal.attributes.clone_ids)),
      this.eventCloneIds$,
    ).pipe(untilComponentDestroyed(this), shareReplay({ bufferSize: 1, refCount: false }))
    this.canClone$ = this.dealViewRaw$.pipe(
      map(dv => dv.deal.deal_id && !dv.deal.attributes.clone_ids?.length),
      distinctUntilChanged())

    this.initServerSidePagination()

    this.showSupplierFilter$ = this.suppliers$.pipe(map(suppliers => suppliers.length > 1), distinctUntilChanged())
    this.showBuyerFilter$ = this.buyers$.pipe(map(buyers => buyers.length > 1), distinctUntilChanged())
    this.showFilters$ = combineLatest([this.showSupplierFilter$, this.showBuyerFilter$]).pipe(map(([a, b]) => a || b))

    this.actions$.pipe(
      ofType(setOriginLocationSuccess, setDestLocationSuccess, massDealFormSuccess, sendConfirmationSuccess, saveDealFormSuccess),
      withLatestFrom(this.cloneIds$),
      untilComponentDestroyed(this),
    ).subscribe(([, deal_ids]) => {
      this.reloadClonesTabDealViews(deal_ids)
    })

    this.actions$.pipe(
      ofType(cloneDealOperationCompleted),
      untilComponentDestroyed(this),
    ).subscribe(action => {
      // the operation runs on the bus and never refreshes the deal view, so its ids are the only
      // current clone set - a later view emission takes over again (WA-22129)
      this.eventCloneIds$.next(action.dealIds)
      this.reloadClonesTabDealViews(action.dealIds)
    })

    this.actions$.pipe(
      ofType(cloneDealSuccess, copyCloneDealSuccess),
      withLatestFrom(this.dealViewRaw$),
      untilComponentDestroyed(this),
    ).subscribe(([action, dv]) => {
      const primary = action.deals.find(d => d.deal_id === dv.deal.deal_id)
      const fromPrimary = primary?.attributes?.clone_ids
      const fromView = dv.deal.attributes?.clone_ids
      const deal_ids = fromPrimary?.length ? fromPrimary : fromView
      this.reloadClonesTabDealViews(deal_ids)
    })
  }

  private initServerSidePagination() {
    // two jobs, two scopes: the dropdowns describe the WHOLE clone set (scoping them to the
    // selection would unmount the control that undoes it), the matched ids describe the selection
    const cloneIds$ = this.cloneIds$.pipe(waitNotEmpty(), distinctUntilChanged(isEqual))
    const wholeSet$ = combineLatest([cloneIds$, this.reloadAggregations$]).pipe(
      switchMap(([cloneIds]) => this.elastic.fetchDealFilters({
        query: { deal_ids: cloneIds as string[] },
        columns: ['deal_ids', 'buyer', 'supplier'],
      }).pipe(
        map(aggregations => ({ cloneIds: cloneIds as string[], aggregations })),
        catchError(() => {
          this.toaster.error('Unable to load the clones.')
          return EMPTY
        }))),
      untilComponentDestroyed(this),
      shareReplay({ bufferSize: 1, refCount: false }))

    this.matched$ = combineLatest([
      wholeSet$,
      replayForm(this.filtersForm).pipe(
        map(({ buyerId, supplierId }) => ({ buyerId, supplierId })),
        distinctUntilChanged(isEqual)),
    ]).pipe(
      switchMap(([{ cloneIds, aggregations }, { buyerId, supplierId }]) => {
        // the aggregation returns matching ids in bucket order - clone_ids carries clone_sequence order
        const matchedIn = (aggs: ElasticSearchFilters) => {
          const matched = new Set(aggs.deal_ids ? optionIds(aggs.deal_ids) : cloneIds)
          return cloneIds.filter(id => matched.has(id))
        }
        const selection = { buyerId, supplierId }
        const scope = pickBy({ buyer: buyerId?.map(String), supplier: supplierId?.map(String) }, value => !isEmpty(value))
        if (isEmpty(scope)) return of({ cloneIds, ids: matchedIn(aggregations), aggregations, selection })
        return this.elastic.fetchDealFilters({
          query: { deal_ids: cloneIds, ...scope },
          columns: ['deal_ids', 'buyer', 'supplier'],
        }).pipe(
          map(scoped => ({ cloneIds, ids: matchedIn(scoped), aggregations, selection })),
          catchError(() => {
            this.toaster.error('Unable to filter the clones.')
            return EMPTY
          }))
      }),
      // refCount: false so the aggregation is shared by the fetch handler and the dropdowns
      // instead of being re-issued per subscriber; untilComponentDestroyed tears it down
      untilComponentDestroyed(this),
      shareReplay({ bufferSize: 1, refCount: false }))

    this.suppliers$ = combineLatest([this.accounts$, this.matched$]).pipe(map(([accounts, { aggregations }]) =>
      sortBy(_map(pick(accounts, optionIds(aggregations.supplier))), 'name')))
    this.buyers$ = combineLatest([this.accounts$, this.matched$]).pipe(map(([accounts, { aggregations }]) =>
      sortBy(_map(pick(accounts, optionIds(aggregations.buyer))), 'name')))

    // rows are read by id, not from the reducer's window: the backend aligns its hits to the
    // ES order, never to the order we sent, so the window would render the page scrambled.
    // Mapping our own ordered ids also gives the full length the paginator needs, with holes
    // for the clones no page has loaded yet.
    this.rows$ = combineLatest([
      this.store.select(selectDealViewEntities),
      this.store.select(selectDealsTable(this.tableIdentity)),
      this.displayIds$,
    ]).pipe(map(([entities, window, displayIds]) => {
      if (!displayIds) return window as DealRow[]
      return displayIds.map(dealId => entities[dealId] as DealRow)
    }))

    this.dataSource = new ApiTableDataSource<DealRow, CloneFiltersFormValue>(
      this.rows$.pipe(filter(identity)),
      this.filtersForm,
      replayForm(this.filtersForm).pipe(distinctUntilChanged(isEqual)),
      { defaultPageSize: 25, pageSizeOptions: [10, 25, 50, 100] })
    const columnDefs = columnDefsById(this.tableIdentity)
    this.dataSource.sortingDataAccessor = (row: DealRow, sortHeaderId: string) =>
      this.csvFormatter.getCellValue(row, columnDefs[sortHeaderId])

    combineLatest([this.dataSource.fetch$, this.matched$, cloneIds$]).pipe(
      // matched$ is shared and replays, so only act on the aggregation computed from THIS fetch's
      // selection and the current clone set. a later aggregation re-fires this and dispatches then,
      // where a take(1) on the replay would already have closed over the previous one
      filter(([{ filters }, matched, cloneIds]) =>
        isEqual(matched.cloneIds, cloneIds) && isEqual(matched.selection, selectionOf(filters))),
      switchMap(([{ filters, page }, matched]) =>
        this.orderedIds$(filters, matched.ids).pipe(take(1), map(ids => ({ ids, filters, page })))),
      untilComponentDestroyed(this),
    ).subscribe(({ ids, filters, page }) => {
      const { limit, columns, sort } = filters
      this.orderedIds = ids
      if (!ids.length) {
        this.displayIds$.next(ids)
        return
      }
      if (FRONTEND_SORT_COLUMNS.includes(sort?.id) || !sort?.id) {
        // we own the order, so we ask for one page of rows and place them ourselves
        this.displayIds$.next(ids)
        this.store.dispatch(loadDealViews({
          tableKey: this.tableIdentity,
          filters: { deal_ids: ids.slice(page * limit, page * limit + limit), limit, columns },
          page: 0,
          calculate: false,
        }))
      } else {
        // indexed column: the backend sorts and pages the whole matched set, so its order wins
        this.displayIds$.next(undefined)
        this.store.dispatch(loadDealViews({
          tableKey: this.tableIdentity,
          filters: { deal_ids: ids, limit, sort, columns },
          page,
          calculate: false,
        }))
      }
    })

    // the data source fires its first fetch from the constructor, before we subscribe above
    this.dataSource.refresh()
  }

  private orderedIds$({ sort }: CloneFiltersFormValue, matched: string[]) {
    // nothing matched, so there is nothing to order - and the Closing Date branch below would fetch
    // with an empty deal_ids, which the backend reads as "no id filter"
    if (!matched.length) return of(matched)
    if (sort?.id === 'closingDate') {
      return this.closingDateOrder$(matched, sort.start)
    }
    if (!sort?.id || sort.id === 'seq') {
      return of(sort?.start === 'desc' ? [...matched].reverse() : matched)
    }
    return of(matched)
  }

  // closingDate is computed on the frontend, so ordering by it needs the inputs for every
  // clone - fetched once per matched set, then paged by slicing the ordered array
  private closingDateOrder$(ids: string[], start: SortDirection) {
    if (!isEqual(this.closingDateFor, ids)) {
      this.closingDateFor = ids
      this.closingDateRows$ = this.elastic.fetchAllDeals({ deal_ids: ids, columns: this.fetchColumns }, ids.length).pipe(
        catchError(() => {
          this.closingDateFor = undefined
          this.toaster.error('Unable to sort the clones by closing date.')
          return EMPTY
        }),
        shareReplay({ bufferSize: 1, refCount: false }))
    }
    return this.closingDateRows$.pipe(map(rows =>
      orderBy(rows, 'closingDate', start === 'desc' ? 'desc' : 'asc').map(row => row.deal_id)))
  }

  private reloadClonesTabDealViews(deal_ids: DeepReadonly<string[] | undefined>) {
    if (!deal_ids?.length) return
    this.closingDateFor = undefined
    this.reloadAggregations$.next()
    this.dataSource.refresh()
  }

  showCloneDeal() {
    this.dealViewRaw$.pipe(take(1)).subscribe(dv =>
      this.CloneForm.showCloneForm(dv))
  }

  get selectedIds$() {
    return this.dealsList.selection.selectedIds$
  }
}
